import { inject, Component, Input } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from '@services/api/api.service';
import { EventsService } from '@services/events/events.service';
import { PaymentV2Service } from '@services/payment-v2/payment-v2.service';
import { StorageService } from '@services/storage/storage.service';
import { UtilService } from '@services/util/util.service';
import { VariablesService } from '@services/variables/variables.service';
import { IOrder } from '@models/order.model';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CurrencyIconComponent } from '@core-components/currency-icon/currency-icon.component';
import { GestureIntroOverlayComponent } from '../gesture-intro-overlay/gesture-intro-overlay.component';

@Component({
  selector: 'app-sip-quiz-question',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    RouterModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    CurrencyIconComponent,
    GestureIntroOverlayComponent
  ],
  templateUrl: './sip-quiz-question.component.html',
  styleUrls: ['./sip-quiz-question.component.scss']
})
export class SipQuizQuestionComponent {

  private events = inject(EventsService);
  public vars = inject(VariablesService);
  public util = inject(UtilService);
  public paymentV2 = inject(PaymentV2Service);
  private activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  public storage = inject(StorageService);

  @Input() order: IOrder;
  @Input() prizeAmount: number;
  public currentQuestionIndex = 0;
  public passedSeconds = 0;
  public showGestureOverlay = true;
  private pressed = false;
  public questions = [
    {
      srNo: 1,
      question: 'What does Ketto’s monthly giving program SIP stand for?',
      step: 1,
      options: [
        {
          indicator: 'A',
          option: 'Systematic Investment Plan',
          isCorrect: false
        },
        {
          indicator: 'B',
          option: 'Social Impact Plan',
          isCorrect: true
        },
        {
          indicator: 'C',
          option: 'Systematic Involvement Program',
          isCorrect: false
        },
        {
          indicator: 'D',
          option: 'None of the above',
          isCorrect: false
        }
      ]
    },
    {
      srNo: 1,
      step: 3,
      info: 'SIP is a monthly giving program that allows its contributors to support over 12 lives every year with as little as INR 100 per month.'
    },
    {
      srNo: 2,
      question: 'How big is the Ketto SIP contributor community?',
      step: 1,
      options: [
        {
          indicator: 'A',
          option: '0-50k contributors',
          isCorrect: false
        },
        {
          indicator: 'B',
          option: '50k-100k contributors',
          isCorrect: true
        },
        {
          indicator: 'C',
          option: '100k-500k contributors',
          isCorrect: false
        },
        {
          indicator: 'D',
          option: '500k+ contributors',
          isCorrect: false
        }
      ]
    },
    {
      srNo: 2,
      step: 3,
      info: 'Since 2019, the Ketto SIP Community has grown significantly, with 50k-100k people currently saving lives every month.'
    },
    {
      srNo: 3,
      question: 'What exclusive rewards does a Ketto SIP contributor receive?',
      step: 1,
      options: [
        {
          indicator: 'A',
          option: 'Teleconsultation',
          isCorrect: false
        },
        // {
        //   indicator: 'B',
        //   option: 'SIP Protect',
        //   isCorrect: false
        // },
        {
          indicator: 'C',
          option: 'Matching Contribution',
          isCorrect: false
        },
        {
          indicator: 'D',
          option: 'All of the above',
          isCorrect: true
        }
      ]
    },
    {
      srNo: 3,
      step: 3,
      info: 'Ketto cares about the well-being of contributors. To reward good deeds, we offer many benefits to help them stay healthy.'
    }
  ];

  private optionClickFlag = false;
  private interval;
  private passedSecondsInterval;
  public selectedAnswer = '';
  public isCorrect = false;
  public lastOrder;
  private remainingSecondsTimeout;
  private movetoNextScreenTimeout;
  private subscription: Subscription = new Subscription();

  ngOnInit() {
    this.vars.lastOrderInfo.subscribe(res => this.lastOrder = res);
    this.activatedRoute.queryParams.subscribe(params => {
      if (params.st) {
        if (params.st === 'f') {
          this.showGestureOverlay = false;
          this.currentQuestionIndex = this.questions.length - 1;
          this.isCorrect = this.vars.lastQuizResponse;
        }
      }
    });
  }
  /**
   * Starts quiz
   */
  public startQuiz(): void {
    this.showGestureOverlay = false;
    this.sendEvent(this.questions[this.currentQuestionIndex].step);
    this.setPassedSecondsInterval();
    this.interval = setInterval(() => {
      this.incrementCurrentQuestionIndex();
    }, 20000);
  }

  /**
   * Increments current question index
   * and resets selected answer
   */
  private incrementCurrentQuestionIndex(): void {
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.currentQuestionIndex += 1;
      this.sendEvent(this.questions[this.currentQuestionIndex].step);
      clearTimeout(this.movetoNextScreenTimeout);
    } else {
      clearInterval(this.interval);
      clearInterval(this.passedSecondsInterval);
    }
    if (this.selectedAnswer === '') {
      this.isCorrect = false;
    }
    if (this.currentQuestionIndex % 2 === 0) {
      this.selectedAnswer = '';
    }
  }

  /**
   * Sends system event
   * @param step question step
   */
  private sendEvent(step: number, isCorrect: string = ''): void {
    this.subscription = this.events.sendSystemEvent({
      eventName: 'sip_quiz',
      info_1: 'question_' + (this.questions[this.currentQuestionIndex].srNo),
      info_2: 'step_' + step + isCorrect,
      info_3: 'impact'
    }).subscribe(_ => _);
  }

  /**
   * Triggers when an option is selected
   * @param selectedOption selected option
   */
  onOptionSelect(selectedOption: string, isCorrect: boolean): void {
    if (!this.selectedAnswer) {
      this.optionClickFlag = true;
      this.selectedAnswer = selectedOption;
      this.isCorrect = isCorrect;
      this.vars.lastQuizResponse = isCorrect;
      this.sendEvent(2, isCorrect ? '_right' : '_wrong');
      this.movetoNextScreenTimeout = setTimeout(() => {
        this.incrementCurrentQuestionIndex();
        this.resetInterval();
        clearTimeout(this.remainingSecondsTimeout);
      }, 800);
    }
  }

  /**
   * Checks answer whether it's correct or incorrect
   * @param option selected option
   * @returns css class
   */
  checkAnswer(option: { indicator: string, option: string, isCorrect: boolean }): string {
    if (this.selectedAnswer !== '') {
      if (option.option === this.selectedAnswer) {
        if (option.isCorrect) {
          return 'correctAnswer';
        } else {
          return this.currentQuestionIndex === 4 ? 'incorrectAnswerForLastQuestion' : 'incorrectAnswer';
        }
      } else {
        if (option.isCorrect) {
          return this.currentQuestionIndex === 4 ? 'blur-option' : 'correctAnswer';
        }
        return 'blur-option';
      }
    }
    return '';
  }

  /**
   * Resets 20 seconds interval
   */
  private resetInterval(): void {
    clearInterval(this.interval);
    clearInterval(this.passedSecondsInterval);
    this.setPassedSecondsInterval();
    this.interval = setInterval(() => {
      this.incrementCurrentQuestionIndex();
    }, 20000);
  }

  /**
   * Sets passed seconds interval
   */
  private setPassedSecondsInterval(): void {
    this.passedSeconds = 0;
    this.passedSecondsInterval = setInterval(() => {
      if (this.passedSeconds === 20) {
        this.passedSeconds = 0;
      }
      this.passedSeconds++;
    }, 1000);
  }

  /**
   * Changes interval state based on click position ie. right or left
   * @param event click event
   */
  changeIntervalState(event): void {
    if (this.currentQuestionIndex !== this.questions.length - 1) {
      if (this.optionClickFlag) {
        this.optionClickFlag = false;
      } else {
        const element = document.getElementById('quiz-wrapper');
        if (event.clientX > (element.clientWidth / 2)) {
          this.incrementCurrentQuestionIndex();
        }
        this.resetInterval();
        clearTimeout(this.remainingSecondsTimeout);
      }
    }
  }

  /**
   * Triggers when long press starts
   */
  onPress(): void {
    clearInterval(this.passedSecondsInterval);
    clearInterval(this.interval);
    clearTimeout(this.remainingSecondsTimeout);
    this.pressed = true;
  }

  /**
   * Triggers when long press ends
   */
  onPressUp(): void {
    if (this.pressed === true) {
      const secondsPassed: number = this.passedSeconds;
      this.passedSecondsInterval = setInterval(() => {
        this.passedSeconds++;
      }, 1000);
      this.remainingSecondsTimeout = setTimeout(() => {
        if (this.currentQuestionIndex === this.questions.length - 1) {
          clearInterval(this.passedSecondsInterval);
        } else {
          this.incrementCurrentQuestionIndex();
          clearInterval(this.passedSecondsInterval);
          this.resetInterval();
        }
      }, (20 - secondsPassed) * 1000);
      this.pressed = false;
    }
  }

  /**
   * Gets progress bar value
   * @param index index of progress bar
   * @returns value between 0 to 100
   */
  getValue(index: number): number {
    if (index === this.questions.length - 1 && this.currentQuestionIndex === this.questions.length - 1) {
      return 100;
    } else {
      if (this.currentQuestionIndex === index) {
        return (this.passedSeconds / 20) * 100;
      }
      if (index < this.currentQuestionIndex) {
        return 100;
      }
      if (index > this.currentQuestionIndex) {
        return 0;
      }
    }
  }

  /**
   * Redeem prize amount and signup for monthly
   */
  redeemAndSignup(): void {
    this.events.sendSystemEvent({
      eventName: 'sip_quiz',
      info_1: 'redeem_and_signup',
      info_2: this.prizeAmount,
      info_3: 'impact'
    }).subscribe(_ => _);
  }

  /**
   * Redeem prize amount
   */
  redeemPrizeAmount(): void {
    this.hideQuiz('sip-quiz');
    document.getElementById('redeem-typ').style.display = 'block';
    this.events.sendSystemEvent({
      eventName: 'sip_quiz',
      info_1: 'redeem',
      info_2: this.prizeAmount,
      info_3: 'impact'
    }).subscribe(_ => _);
  }

  /**
   * Hides quiz
   */
  hideQuiz(elementId: string): void {
    this.vars.isQuizCompleted = true;
    this.storage.addSessionData('isQuizCompleted', true.toString());
    document.getElementById(elementId).style.display = 'none';
    this.storage.addSessionData('showQuiz', false.toString());
    document.getElementById('sip-quiz-wrapper').style.display = 'none';
  }

  /**
   * On payment button click
   */
  onPaymentClick(): void {
    this.api.applyCoupon(this.order.campaign_id, { coupon_id: 24 })
      .subscribe(() => { });
    this.events.sendSystemEvent({
      eventName: 'give_monthly_new',
      info_1: 'position',
      info_2: '43',
      info_3: 'impact',
    }).subscribe(_ => _);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
